export type ChatRole = 'user' | 'assistant';

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  createdAt: number;
  error?: boolean;
}

/**
 * Streams the assistant's reply as text chunks.
 *
 * Frontend-only placeholder: replies are canned until the API exposes a chat endpoint.
 * Swap the body for a streaming fetch to that endpoint — the panel only depends on this signature.
 */
export async function* streamReply(history: ChatMessage[], signal: AbortSignal): AsyncGenerator<string> {
  const last = history[history.length - 1]?.content ?? '';
  const reply = mockReply(last);
  await wait(450, signal);
  for (const token of reply.match(/\S+\s*/g) ?? []) {
    await wait(25 + Math.random() * 40, signal);
    yield token;
  }
}

function mockReply(prompt: string): string {
  const p = prompt.toLowerCase();
  if (p.includes('leave')) {
    return "I'll be able to check your leave balances and draft leave requests once I'm connected to the HRMS backend.\n\nFor now, you can open **Self service → Leave** to see your balances and apply for leave.";
  }
  if (p.includes('attendance') || p.includes('check in') || p.includes('check-in')) {
    return 'Soon I will summarise your attendance and flag missed check-ins. Until then, head to **Attendance → My attendance** to check in, check out, or review your history.';
  }
  if (p.includes('holiday') || p.includes('calendar')) {
    return "Holiday calendars are managed under **Organization → Calendars**. Once I'm wired up, I'll answer questions like \"When is the next holiday?\" directly.";
  }
  return "Thanks! I'm the HRMS assistant — this is a preview of the chat experience and I'm not connected to a model yet.\n\nOnce the backend is ready I'll help with leave, attendance, onboarding, assets and policy questions.";
}

function wait(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException('Aborted', 'AbortError'));
    const t = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(t);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}
